/**
 * Things on the room walk that are not on our list (owner, 2026-10-13: "what if we identify equipment and objects that are not in
 * the list … a podium or the POS system that should be wiped down … so it's already ready to go"). A room photo may name an object
 * as itemType "other" with its plain name ("POS terminal", "host stand"). It is never dropped:
 *
 *  O1 Nearest guide first. A name the alias table knows (a POS, a card reader, a register, a kiosk → "Payment screens and POS";
 *     menus; door handles; windows) takes that guide. Else the Things library (objectops-data.ts, business types) or the surface
 *     library (surfaces.ts) when EVERY one of the name's own words (not words like "stand" or "station") is in a guide's name or
 *     aliases, and the name ends in one of them (what it is, not what it's like): a "coffee station" is not the espresso machine,
 *     a "tip jar" not the blender jars. A wrong guide teaches wrong steps; no guide shows the general rule (O2), so we stay strict.
 *  O2 No guide: the row stays as the person's own thing ("Host stand"), with the general rule for a surface food never touches,
 *     quoted from surfaces.ts (NFC_CLEAN, Food Code 4-601.11(C)) plus 4-602.13 (cleaned often enough that soil doesn't build up;
 *     docs/source/research/templates-and-logs-excerpts.txt). No steps of our own: nothing here is written that no source says.
 *  O3 The team hears about it: the API counts each object with no guide as a content ticket (services/api setupscan/pipeline.ts,
 *     core content.ts), so the most-seen gaps get a guide first. The ticket holds the object's name only, never the business's.
 *  O4 The model never decides the guide: these pure functions do (setupscan.ts T3).
 *
 * Tests: packages/core/test/walkobjects.test.ts.
 */
import { OBJECT_TYPES } from "./objectops-data.ts";
import { getSurface, NFC_CLEAN, SURFACES } from "./surfaces.ts";

/** The walk item type for "something not on our list" (roomwalk.ts WALK_ITEMS). */
export const OTHER_TYPE = "other";
/** The longest name an object keeps. */
export const OTHER_NAME_MAX = 60;

/** A guide an object is cleaned by: a Things library type id ("microwave") or a surface guide ("surface:payment-screen"). */
export interface ObjectGuide { id: string; name: string }

/** O2: what an object with no guide shows, from a sourced line (never steps of our own). */
export const OTHER_GENERIC = {
  line: `No cleaning guide for this yet. ${NFC_CLEAN.when}, on your cleaning schedule.`,
  cite: `${NFC_CLEAN.citation}, 4-602.13`,
} as const;

const norm = (s: string) => s.toLowerCase().normalize("NFKD").replace(/[^a-z0-9]+/g, " ").trim();
const stem = (w: string) => (w.length > 4 && w.endsWith("es") && /(sh|ch|x|s)es$/.test(w) ? w.slice(0, -2) : w.length > 3 && w.endsWith("s") && !w.endsWith("ss") ? w.slice(0, -1) : w);
/** Words that say what shape a thing is, not what it is: a "host stand" is not a stand mixer, a "menu table" not a dining table. */
const GENERIC = new Set(["stand", "station", "table", "case", "unit", "machine", "cart", "rack", "box", "holder", "area", "counter", "top", "display", "small", "large", "big", "old", "new", "wood", "wooden", "metal", "plastic", "glass", "steel", "stainless", "black", "white", "front", "back", "side", "wall", "the", "a", "an", "and", "of", "for", "with", "on", "in"]);
const words = (s: string) => norm(s).split(" ").filter((w) => w.length > 1).map(stem);

/** O1: the names people and photos use for things a guide already covers. Checked in order; first hit wins. */
const ALIASES: [RegExp, string][] = [
  [/\b(pos|point of sale|cash registers?|registers?|card readers?|card terminals?|credit card (machine|reader|terminal)s?|payment (terminal|screen|pad|device|reader)s?|pin ?pads?|kiosks?|ordering (screen|tablet)s?|tablets?|ipads?|touch ?screens?)\b/, "surface:payment-screen"],
  [/\b(menus?|menu (holder|rack|board)s?|table tents?)\b/, "surface:menus"],
  [/\b(door handles?|door ?knobs?|push plates?|pull handles?|light switch(es)?)\b/, "surface:door-handles"],
  [/\b(windows?|glass doors?|mirrors?)\b/, "surface:windows-glass"],
];

const nameOf = (id: string): string | undefined => (id.startsWith("surface:") ? getSurface(id.slice(8))?.name : OBJECT_TYPES.find((t) => t.id === id)?.name);

/** O1: the share of the name's own words a candidate's words hold (1: all of them). */
function fit(own: string[], hay: Set<string>): number {
  if (!own.length) return 0;
  const hits = own.filter((w) => hay.has(w)).length;
  return hits / own.length;
}

/**
 * The guide an object not on our list is cleaned by (O1), or null (O2). Pure; the same name always gets the same guide.
 * "POS terminal" → Payment screens and POS; "Toaster" → Toaster; "Host stand" → null.
 */
export function objectGuideFor(name: string | null | undefined): ObjectGuide | null {
  const n = norm(name ?? "");
  if (!n) return null;
  for (const [re, id] of ALIASES) if (re.test(n)) { const g = nameOf(id); if (g) return { id, name: g }; }
  const all = words(n);
  const own = all.filter((w) => !GENERIC.has(w));
  // What it is: the name's last word ("host STAND", "coffee STATION" say shape only, so they have no library guide).
  if (!own.length || GENERIC.has(all[all.length - 1]!)) return null;
  let best: { id: string; name: string; score: number } | undefined;
  for (const t of OBJECT_TYPES) {
    if (!t.business) continue;
    const s = fit(own, new Set([t.name, ...t.aliases].flatMap(words)));
    if (s === 1 && !best) best = { id: t.id, name: t.name, score: s };
  }
  for (const g of SURFACES) {
    if (g.reference) continue;
    const s = fit(own, new Set([g.name, ...g.keywords, ...(g.materials ?? [])].flatMap(words)));
    if (s === 1 && !best) best = { id: `surface:${g.id}`, name: g.name, score: s };
  }
  return best ? { id: best.id, name: best.name } : null;
}

/**
 * The format characters that spoof how a name reads (re-audit 2026-10-15): zero-width space, LRM/RLM, bidi embeddings and overrides,
 * word joiner, bidi isolates, the byte-order mark. Never the joiners words and emoji need: ZWNJ U+200C (Persian), ZWJ U+200D (emoji
 * sequences, Indic), the Arabic letter mark U+061C, the soft hyphen U+00AD.
 */
export const SPOOF_CHARS = /[\u200B\u200E\u200F\u202A-\u202E\u2060\u2066-\u2069\uFEFF]+/g;
/**
 * A typed name as one plain line (security audit 2026-10-15, preview touch): the spoofing characters (SPOOF_CHARS) are dropped; control characters, line and paragraph separators
 * become a space; spaces collapse. HTML stays as typed: every place that renders a name escapes it. The ONE cleaner for a typed room
 * or thing name, at every door (the walk, the owner's review, the preview link).
 */
export const plainName = (v: unknown): string => (typeof v === "string" ? v.replace(SPOOF_CHARS, "").replace(/[\p{Cc}\u2028\u2029]+/gu, " ").replace(/\s+/g, " ").trim() : "");
/** An object's name as kept: one line, plain, at most OTHER_NAME_MAX characters; "" when there is nothing to keep. */
export const cleanObjectName = (v: unknown): string => plainName(v).slice(0, OTHER_NAME_MAX);

/** The line an object row shows: its guide, or the general rule (O2). */
export function objectGuideLine(g: ObjectGuide | null | undefined): { line: string; cite?: string } {
  return g ? { line: `Cleaned with the guide: ${g.name}` } : { line: OTHER_GENERIC.line, cite: OTHER_GENERIC.cite };
}
